import { ChevronRight } from "lucide-react";
import { useNavigationState } from "./hooks/useNavigationState";
import { useResolvedNavigation } from "./hooks/useResolvedNavigation";
import { NavigationHeader } from "./components/NavigationHeader";
import { NavSearchTrigger } from "./components/NavSearchTrigger";
import { NavigationMenu } from "./components/NavigationMenu";
import { SetupTracker } from "./components/SetupTracker";
import { NavigationFooter } from "./components/NavigationFooter";
import LogoutModal from "./components/LogoutModal";
import { useState } from "react";
import "./Navigation.scss";

/**
 * Main (dashboard) navigation rail.
 *
 * Collapsed to an icon rail by default, purely click-driven (no
 * hover-to-peek). Two separate, single-purpose controls toggle it:
 * - Expanded: the chevron in the header row (`nav-header__collapse-btn`,
 *   where the old "+" quick-actions button used to sit) collapses it.
 * - Collapsed: a round button straddling the rail's own right edge
 *   (`nav__expand-toggle`, half poking past the border) expands it.
 * Either click locks it open and reserves real layout space via the
 * `nav--locked` class (see AppLayout.scss), or releases that lock.
 */
const Navigation = () => {
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);

  const handleLogoutClick = () => {
    setIsLogoutOpen(true);
  };

  const currentNavItems = useResolvedNavigation(false);

  const {
    isExpanded,
    footerRef,
    handlers: {
      handleToggleLock,
    },
  } = useNavigationState();

  const mini = !isExpanded;

  return (
    <div
      className={`nav ${isExpanded ? "nav--expanded" : "nav--collapsed"} ${isExpanded ? "nav--locked" : ""}`}
    >
      <NavigationHeader
        mini={mini}
        onToggleCollapse={handleToggleLock}
      />
      {mini && (
        <button
          type="button"
          className="nav__expand-toggle"
          onClick={(e) => {
            e.stopPropagation();
            handleToggleLock();
          }}
          title="Expand sidebar"
          aria-label="Expand sidebar"
        >
          <ChevronRight size={14} />
        </button>
      )}
      <NavSearchTrigger mini={mini} />
      <NavigationMenu
        currentNavItems={currentNavItems}
        mini={mini}
      />
      <div className="nav__footer-group">
        {!mini && <SetupTracker />}
        <NavigationFooter
          ref={footerRef}
          onLogoutClick={handleLogoutClick}
          mini={mini}
        />
      </div>
      <LogoutModal isOpen={isLogoutOpen} onClose={() => setIsLogoutOpen(false)} />
    </div>
  );
};

export default Navigation;
